type BaseWebSocketResponse<TType, TPayload = never> = {
  type: TType;
  payload: TPayload;
  requestId?: string;
};

type CompletePayload = {
  content: string;
  sessionId: string;
};

type ChunkPayload = {
  content: string;
};

type StatusPayload = {
  code: string;
  tool: string;
};

type UnknownWebSocketError = {
  connectionId: string;
  message: string;
  requestId: string;
};

export enum WebSocketErrorCode {
  ISE = 'err:mcp.internal-server-error',
  AUTH_FAILURE = 'err:mcp.auth-failure',
  UNKNOWN_ROUTE = 'err:mcp.unknown-route',
}

type ErrorPayload = {
  content: string;
  code: WebSocketErrorCode | string;
  sessionId?: string;
};

export type WebSocketAIMessage =
  | BaseWebSocketResponse<'status', StatusPayload>
  | BaseWebSocketResponse<'complete', CompletePayload>
  | BaseWebSocketResponse<'chunk', ChunkPayload>;

export type WebSocketResponse = WebSocketAIMessage | UnknownWebSocketError;

export type WebSocketError = BaseWebSocketResponse<'error', ErrorPayload>;

export const isUnknownWebSocketMessage = (
  e: UnknownWebSocketError | WebSocketAIMessage,
): e is UnknownWebSocketError => (e as WebSocketAIMessage).type == null;

export const isWebSocketError = (e: WebSocketResponse | WebSocketError): e is WebSocketError =>
  (e as WebSocketError).type === 'error';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v != null;

const str = (v: unknown): v is string => typeof v === 'string';

const optStr = (v: unknown) => v == null || str(v);

const payload = (v: Record<string, unknown>) => (isObj(v.payload) ? v.payload : null);

export const parseWebSocketResponse = (
  value: unknown,
): WebSocketResponse | WebSocketError | null => {
  if (!isObj(value)) return null;

  if (str(value.connectionId) && str(value.message) && str(value.requestId)) {
    return value as UnknownWebSocketError;
  }

  const type = value.type;
  const p = payload(value);
  if (p == null || !optStr(value.requestId)) return null;

  switch (type) {
    case 'chunk':
      return str(p.content) ? (value as BaseWebSocketResponse<'chunk', ChunkPayload>) : null;
    case 'complete':
      return str(p.content) && str(p.sessionId)
        ? (value as BaseWebSocketResponse<'complete', CompletePayload>)
        : null;
    case 'status':
      return str(p.code) && str(p.tool)
        ? (value as BaseWebSocketResponse<'status', StatusPayload>)
        : null;
    case 'error':
      return str(p.content) && str(p.code) && optStr(p.sessionId)
        ? (value as WebSocketError)
        : null;
    default:
      return null;
  }
};
